import { Component, EventEmitter, HostListener, Output, computed, input, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { UiButtonComponent } from '../../shared/ui';
import { RecipeFacetsCatalogue } from '../../services/recipe/recipe-facets.models';

type FacetAxis = 'course' | 'season' | 'dishType';

/**
 * Filter bar for the recipe list's classification facets (course / season /
 * dish type). Replaces the three stacked <select> boxes with a single row of
 * pill "facet menus": each pill opens a checklist, the active pick surfaces as
 * a removable colour-coded chip, and a Zurücksetzen link clears everything.
 *
 * Filtering is by KEY; labels are read from the catalogue (dish types fall back
 * name_de -> name_en -> key). Each axis is currently single-select to match the
 * list endpoint's single-value query params — flipping season/dish type to
 * multi-select is a local change here once the API accepts repeated keys.
 */
@Component({
  selector: 'app-recipe-facet-filter-bar',
  standalone: true,
  imports: [FontAwesomeModule, UiButtonComponent],
  template: `
    @if (catalogue(); as cat) {
      <div class="flex flex-wrap items-center gap-2">
        <!-- Course -->
        <div class="relative">
          <button
            type="button"
            class="facet-pill"
            [class.facet-pill--active]="!!course()"
            [attr.aria-expanded]="openFacet() === 'course'"
            aria-haspopup="true"
            [disabled]="disabled()"
            (click)="toggleMenu('course', $event)">
            <span>{{ course() ? courseLabel() : 'Gang' }}</span>
            <fa-icon class="facet-pill__caret" [icon]="faChevronDown"></fa-icon>
          </button>

          @if (openFacet() === 'course') {
            <div class="facet-menu" (click)="$event.stopPropagation()">
              <div class="facet-menu__label">Gang</div>
              @for (opt of cat.courses; track opt.key) {
                <button type="button" class="facet-opt" [attr.aria-selected]="course() === opt.key"
                  (click)="pick('course', opt.key)">
                  <span class="facet-opt__mark facet-opt__mark--radio">
                    <fa-icon [icon]="faCheck"></fa-icon>
                  </span>
                  <span class="flex-1">{{ opt.label }}</span>
                </button>
              }
            </div>
          }
        </div>

        <!-- Season -->
        <div class="relative">
          <button
            type="button"
            class="facet-pill"
            [class.facet-pill--active]="!!season()"
            [attr.aria-expanded]="openFacet() === 'season'"
            aria-haspopup="true"
            [disabled]="disabled()"
            (click)="toggleMenu('season', $event)">
            <span>{{ season() ? seasonLabel() : 'Saison' }}</span>
            <fa-icon class="facet-pill__caret" [icon]="faChevronDown"></fa-icon>
          </button>

          @if (openFacet() === 'season') {
            <div class="facet-menu" (click)="$event.stopPropagation()">
              <div class="facet-menu__label">Saison</div>
              @for (opt of cat.seasons; track opt.key) {
                <button type="button" class="facet-opt" [attr.aria-selected]="season() === opt.key"
                  (click)="pick('season', opt.key)">
                  <span class="facet-opt__mark facet-opt__mark--radio">
                    <fa-icon [icon]="faCheck"></fa-icon>
                  </span>
                  <span class="flex-1">{{ opt.label }}</span>
                </button>
              }
            </div>
          }
        </div>

        <!-- Dish type -->
        <div class="relative">
          <button
            type="button"
            class="facet-pill"
            [class.facet-pill--active]="!!dishType()"
            [attr.aria-expanded]="openFacet() === 'dishType'"
            aria-haspopup="true"
            [disabled]="disabled()"
            (click)="toggleMenu('dishType', $event)">
            <span>{{ dishType() ? dishTypeLabel() : 'Art' }}</span>
            <fa-icon class="facet-pill__caret" [icon]="faChevronDown"></fa-icon>
          </button>

          @if (openFacet() === 'dishType') {
            <div class="facet-menu" (click)="$event.stopPropagation()">
              <div class="facet-menu__label">Art</div>
              @for (opt of cat.dish_types; track opt.key) {
                <button type="button" class="facet-opt" [attr.aria-selected]="dishType() === opt.key"
                  (click)="pick('dishType', opt.key)">
                  <span class="facet-opt__mark facet-opt__mark--radio">
                    <fa-icon [icon]="faCheck"></fa-icon>
                  </span>
                  <span class="flex-1">{{ dishTypeOptLabel(opt) }}</span>
                </button>
              }
            </div>
          }
        </div>

        <!-- Active-filter chips -->
        @if (hasActive()) {
          <div class="flex flex-wrap items-center gap-1.5">
            @if (course()) {
              <span class="facet-chip facet-chip--course">
                <span>{{ courseLabel() }}</span>
                <button type="button" aria-label="Gang entfernen" (click)="pick('course', course())">
                  <fa-icon [icon]="faXmark"></fa-icon>
                </button>
              </span>
            }
            @if (season()) {
              <span class="facet-chip facet-chip--season">
                <span>{{ seasonLabel() }}</span>
                <button type="button" aria-label="Saison entfernen" (click)="pick('season', season())">
                  <fa-icon [icon]="faXmark"></fa-icon>
                </button>
              </span>
            }
            @if (dishType()) {
              <span class="facet-chip facet-chip--dishtype">
                <span>{{ dishTypeLabel() }}</span>
                <button type="button" aria-label="Art entfernen" (click)="pick('dishType', dishType())">
                  <fa-icon [icon]="faXmark"></fa-icon>
                </button>
              </span>
            }
          </div>

          <ui-button variant="ghost" size="sm" [disabled]="disabled()" (clicked)="clear.emit()">
            Zurücksetzen
          </ui-button>
        }
      </div>
    }
  `,
  styles: [`
    :host { display: contents; }

    .facet-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      border: 1px solid var(--color-base-300);
      background: var(--color-base-100);
      color: color-mix(in oklch, var(--color-base-content) 65%, transparent);
      border-radius: 999px;
      padding: 0.3rem 0.75rem;
      font-size: 0.8125rem;
      font-weight: 600;
      line-height: 1.2;
      cursor: pointer;
      transition: border-color .12s, background-color .12s, color .12s;
    }
    .facet-pill:hover:not(:disabled) {
      border-color: color-mix(in oklch, var(--color-accent) 45%, transparent);
      color: var(--color-base-content);
    }
    .facet-pill:disabled { opacity: .55; cursor: not-allowed; }
    .facet-pill--active {
      border-color: color-mix(in oklch, var(--color-accent) 45%, transparent);
      background: color-mix(in oklch, var(--color-accent) 12%, transparent);
      color: var(--color-base-content);
    }
    .facet-pill__caret { font-size: 0.7rem; opacity: .6; transition: transform .15s; }
    .facet-pill[aria-expanded="true"] .facet-pill__caret { transform: rotate(180deg); }

    .facet-menu {
      position: absolute;
      top: calc(100% + 0.375rem);
      left: 0;
      z-index: 30;
      min-width: 12rem;
      background: var(--color-base-100);
      border: 1px solid var(--color-base-300);
      border-radius: var(--radius-box, 0.5rem);
      box-shadow: 0 8px 24px -12px rgba(0,0,0,.35);
      padding: 0.375rem;
    }
    .facet-menu__label {
      font-size: 0.625rem;
      letter-spacing: .1em;
      text-transform: uppercase;
      font-weight: 700;
      color: color-mix(in oklch, var(--color-base-content) 45%, transparent);
      padding: 0.375rem 0.5rem 0.25rem;
    }
    .facet-opt {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      width: 100%;
      text-align: left;
      border: 0;
      background: transparent;
      color: var(--color-base-content);
      font-size: 0.84rem;
      padding: 0.4rem 0.5rem;
      border-radius: var(--radius-field, 0.25rem);
      cursor: pointer;
    }
    .facet-opt:hover { background: var(--color-base-200); }
    .facet-opt__mark {
      width: 1rem;
      height: 1rem;
      flex: none;
      display: grid;
      place-items: center;
      border: 1.5px solid var(--color-base-300);
      border-radius: 4px;
      color: var(--color-accent-content);
      font-size: 0.6rem;
    }
    .facet-opt__mark--radio { border-radius: 999px; }
    .facet-opt__mark fa-icon { opacity: 0; }
    .facet-opt[aria-selected="true"] .facet-opt__mark {
      background: var(--color-accent);
      border-color: var(--color-accent);
    }
    .facet-opt[aria-selected="true"] .facet-opt__mark fa-icon { opacity: 1; }

    .facet-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.78rem;
      font-weight: 600;
      padding: 0.2rem 0.35rem 0.2rem 0.6rem;
      border-radius: 999px;
      border: 1px solid transparent;
      white-space: nowrap;
    }
    .facet-chip button {
      border: 0;
      background: transparent;
      cursor: pointer;
      color: currentColor;
      opacity: .6;
      padding: 0;
      display: grid;
      place-items: center;
      font-size: 0.7rem;
      line-height: 0;
    }
    .facet-chip button:hover { opacity: 1; }
    .facet-chip--course {
      background: color-mix(in oklch, var(--color-primary) 14%, transparent);
      color: var(--color-primary);
      border-color: color-mix(in oklch, var(--color-primary) 26%, transparent);
    }
    .facet-chip--season {
      background: color-mix(in oklch, var(--color-info) 14%, transparent);
      color: var(--color-info);
      border-color: color-mix(in oklch, var(--color-info) 30%, transparent);
    }
    .facet-chip--dishtype {
      background: var(--color-base-200);
      color: color-mix(in oklch, var(--color-base-content) 70%, transparent);
      border-color: var(--color-base-300);
    }
  `],
})
export class RecipeFacetFilterBarComponent {
  readonly faChevronDown = faChevronDown;
  readonly faCheck = faCheck;
  readonly faXmark = faXmark;

  readonly catalogue = input<RecipeFacetsCatalogue | null>(null);
  readonly course = input('', { transform: (v: string | null | undefined) => v ?? '' });
  readonly season = input('', { transform: (v: string | null | undefined) => v ?? '' });
  readonly dishType = input('', { transform: (v: string | null | undefined) => v ?? '' });
  readonly disabled = input(false, { transform: (v: boolean | null | undefined) => !!v });

  /** Which facet menu is open, or null. Only one open at a time. */
  readonly openFacet = signal<FacetAxis | null>(null);

  @Output() courseChange = new EventEmitter<string>();
  @Output() seasonChange = new EventEmitter<string>();
  @Output() dishTypeChange = new EventEmitter<string>();
  @Output() clear = new EventEmitter<void>();

  readonly hasActive = computed(() => !!(this.course() || this.season() || this.dishType()));

  readonly courseLabel = computed(() =>
    this.catalogue()?.courses.find(c => c.key === this.course())?.label ?? this.course());
  readonly seasonLabel = computed(() =>
    this.catalogue()?.seasons.find(s => s.key === this.season())?.label ?? this.season());
  readonly dishTypeLabel = computed(() => {
    const opt = this.catalogue()?.dish_types.find(c => c.key === this.dishType());
    return opt ? (opt.name_de || opt.name_en || opt.key) : this.dishType();
  });

  dishTypeOptLabel(opt: { key: string; name_de: string; name_en: string }): string {
    return opt.name_de || opt.name_en || opt.key;
  }

  toggleMenu(axis: FacetAxis, event: Event): void {
    event.stopPropagation();
    this.openFacet.set(this.openFacet() === axis ? null : axis);
  }

  /**
   * Single-select toggle: re-picking the active key clears the axis. Emits the
   * new key ('' when cleared) and closes the menu.
   */
  pick(axis: FacetAxis, key: string): void {
    const current = axis === 'course' ? this.course() : axis === 'season' ? this.season() : this.dishType();
    const next = current === key ? '' : key;
    if (axis === 'course') this.courseChange.emit(next);
    else if (axis === 'season') this.seasonChange.emit(next);
    else this.dishTypeChange.emit(next);
    this.openFacet.set(null);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.openFacet() !== null) {
      this.openFacet.set(null);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.openFacet() !== null) {
      this.openFacet.set(null);
    }
  }
}